import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Eye, Pause, Play, Plus, Trash2 } from "lucide-react";
import { DataTableWithFilters } from "@/components/ui/DataTableWithFilter";
import { Button, ConfirmationDialog, Modal, useToast } from "@/components/ui";
import {
  callingAgentsService,
  type CallingAgent,
} from "@/services/api/callingAgents";
import EditAgentModal from "./modals/EditAgentModal";
import { capitalize, providerLabel } from "./components/useVoiceOptions";
import { DEFAULT_PAGE_SIZE } from "@/helpers";

const ManageAgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [agents, setAgents] = useState<CallingAgent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [agentTypeTab, setAgentTypeTab] = useState<"all" | "inbound" | "outbound">(
    "all",
  );
  const [sortState, setSortState] = useState<{
    by?: string;
    dir?: "asc" | "desc";
  }>({});

  const [editingAgent, setEditingAgent] = useState<CallingAgent | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<CallingAgent | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewAgent, setPreviewAgent] = useState<CallingAgent | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);

  const stopVoice = () => {
    voiceAudioRef.current?.pause();
    voiceAudioRef.current = null;
    setIsVoicePlaying(false);
  };

  const previewVoice = previewAgent?.voice_details?.[0];

  const toggleVoice = () => {
    if (voiceAudioRef.current) return stopVoice();
    if (!previewVoice?.previewUrl) return;
    const audio = new Audio(previewVoice.previewUrl);
    audio.onended = stopVoice;
    audio.play().catch(() => {
      stopVoice();
      toastError("Unable to play voice sample");
    });
    voiceAudioRef.current = audio;
    setIsVoicePlaying(true);
  };

  const handleSearch = useCallback((q: string) => {
    setSearch(q);
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((nextItemsPerPage: number) => {
    setItemsPerPage(nextItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((col: string, dir: 'asc' | 'desc') => {
    setSortState({ by: col, dir });
    setCurrentPage(1);
  }, []);

  const fetchAgents = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await callingAgentsService.listing({
        page: currentPage,
        page_size: itemsPerPage,
        search: search || undefined,
        sort: sortState.by
          ? { [sortState.by]: sortState.dir ?? "asc" }
          : { created_at: "desc" },
      });
      const list: CallingAgent[] =
        res?.attributes?.callingAgents ??
        res?.attributes?.data ??
        res?.data ??
        [];
      const total: number = res?.attributes?.pagination?.total ?? list.length;
      setAgents(list);
      setTotalItems(total);
    } catch (err: any) {
      toastError(err?.message || "Failed to load agents");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, search, sortState]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const rows = useMemo(
    () =>
      agents.map((a) => ({
        id: a.uuid,
        display_name: a.display_name,
        type: a.type,
        voice_provider: a.voice_provider,
        language: a.language,
        assistant_id: a.assistant_id ?? "Pending",
        first_message: a.settings?.first_message ?? "-",
        created_at: a.created_at ? new Date(a.created_at).toLocaleString() : "-",
        __raw: a,
      })),
    [agents],
  );
  const inboundCount = useMemo(
    () => rows.filter((row) => row.type === "inbound").length,
    [rows],
  );
  const outboundCount = useMemo(
    () => rows.filter((row) => row.type === "outbound").length,
    [rows],
  );
  const filteredRows = useMemo(() => {
    if (agentTypeTab === "all") return rows;
    return rows.filter((row) => row.type === agentTypeTab);
  }, [rows, agentTypeTab]);
  const tableTotalItems = agentTypeTab === "all" ? totalItems : filteredRows.length;

  const columns = [
    { label: "Display Name", key: "display_name", sortable: true },
    {
      label: "Type",
      key: "type",
      render: (v: string) => (
        <span className="capitalize text-xs font-medium text-gray-700">
          {v}
        </span>
      ),
    },
    {
      label: "Voice Provider",
      key: "voice_provider",
      render: (v: string) => (
        <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
          {v ? providerLabel(v) : "-"}
        </span>
      ),
    },
    {
      label: "Language",
      key: "language",
      render: (v: string) => (
        <span className="uppercase text-xs font-medium text-gray-600">{v}</span>
      ),
    },
    { label: "Assistant ID", key: "assistant_id" },
    {
      label: "First Message",
      key: "first_message",
      render: (message: string) => (
        <span className="line-clamp-2 max-w-xs text-xs text-gray-600">
          {message}
        </span>
      ),
    },
    { label: "Created At", key: "created_at", sortable: true },
    {
      label: "Actions",
      key: "actions",
      render: (_: any, row: any) => (
        <div className="flex gap-2">
          <button onClick={() => handlePreview(row.__raw)} title="Preview">
            <Eye size={16} className="text-sky-600" />
          </button>
          <button
            onClick={() => {
              setEditingAgent(row.__raw);
              setShowEditModal(true);
            }}
            title="Edit"
          >
            <Edit size={16} className="text-primary-button" />
          </button>
          <button
            onClick={() => {
              setAgentToDelete(row.__raw);
              setIsConfirmOpen(true);
            }}
            title="Delete"
          >
            <Trash2 size={16} className="text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!agentToDelete) return;
    await callingAgentsService.remove(agentToDelete.uuid);
    success("AI-Agent deleted successfully");
    setIsConfirmOpen(false);
    setAgentToDelete(null);
    fetchAgents();
  };

  const handlePreview = async (agent: CallingAgent) => {
    setShowPreviewModal(true);
    setPreviewAgent(agent);
    setIsPreviewLoading(true);
    try {
      const res = await callingAgentsService.getById(agent.uuid);
      const details: CallingAgent = res?.attributes ?? res?.data ?? agent;
      setPreviewAgent(details);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            AI-Agents Management
          </h1>
          <p className="text-xs sm:text-sm text-gray-600">
            View and manage all AI calling agents.
          </p>
        </div>
        <Button
          onClick={() => navigate("/add-ai-agent")}
          className="w-full sm:w-auto bg-primary-button hover:bg-primary-button text-white font-bold px-4 py-2 rounded-lg flex justify-center items-center gap-2"
        >
          <Plus size={16} /> Add AI-Agent
        </Button>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="mb-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAgentTypeTab("all")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${
              agentTypeTab === "all"
                ? "bg-primary-button text-white border-primary-button"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            All ({rows.length})
          </button>
          <button
            type="button"
            onClick={() => setAgentTypeTab("inbound")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${
              agentTypeTab === "inbound"
                ? "bg-primary-button text-white border-primary-button"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            Inbound ({inboundCount})
          </button>
          <button
            type="button"
            onClick={() => setAgentTypeTab("outbound")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${
              agentTypeTab === "outbound"
                ? "bg-primary-button text-white border-primary-button"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            Outbound ({outboundCount})
          </button>
        </div>
        <DataTableWithFilters
          title="AI-Agents"
          columns={columns}
          data={filteredRows}
          totalItems={tableTotalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={handleItemsPerPageChange}
          showFilters={false}
          loading={isLoading}
          onSearch={handleSearch}
          onSort={handleSort}
          searchDebounceMs={500}
        />
      </div>

      <EditAgentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingAgent(null);
        }}
        agent={editingAgent}
        onSuccess={fetchAgents}
      />

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setAgentToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete AI-Agent"
        message={`Are you sure you want to delete "${agentToDelete?.display_name ?? "this AI-agent"}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="error"
      />
      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          stopVoice();
          setShowPreviewModal(false);
          setPreviewAgent(null);
        }}
        title="Agent Preview"
        size="lg"
      >
        {isPreviewLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">
            Loading agent details...
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase text-gray-400">Display Name</p>
                <p className="font-medium text-gray-800">
                  {previewAgent?.display_name || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">Type</p>
                <p className="font-medium text-gray-800 capitalize">
                  {previewAgent?.type || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">Voice Provider</p>
                <p className="font-medium text-gray-800">
                  {previewAgent?.voice_provider
                    ? providerLabel(previewAgent.voice_provider)
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">Language</p>
                <p className="font-medium text-gray-800 uppercase">
                  {previewAgent?.language || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">Voice</p>
                {previewVoice ? (
                  <div className="mt-1 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    {previewVoice.previewUrl && (
                      <button
                        type="button"
                        onClick={toggleVoice}
                        aria-label={isVoicePlaying ? "Pause voice sample" : "Play voice sample"}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-button text-white hover:opacity-80"
                      >
                        {isVoicePlaying ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-800">{previewVoice.name}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {[previewVoice.gender, previewVoice.accent]
                          .filter(Boolean)
                          .map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full bg-white px-2 py-0.5 text-xs text-gray-600 ring-1 ring-gray-200"
                            >
                              {capitalize(tag as string)}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="font-medium text-gray-800">Not available</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-400">First Message</p>
              <p className="text-gray-700">
                {previewAgent?.settings?.first_message || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-400">Prompt</p>
              <pre className="mt-1 bg-gray-50 rounded-md p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-60 overflow-auto">
                {previewAgent?.prompt || "-"}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ManageAgentsPage;
